import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { Item, Menu } from '@calendly/ui/components/menu';
import { TextInput } from '@calendly/ui/components/text-input';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { nanoid } from 'nanoid';
import PropTypes from 'prop-types';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { timeNotationSelector } from 'selectors/current_user';
import { formatTime } from 'services/time_format';
import { Input } from './styles';
const minuteStep = 15;
const intervalsPerHour = 60 / minuteStep;
const minutesArray = Array.from({
  length: intervalsPerHour
}, (_, index) => index * minuteStep);
const buildTimes = hour => minutesArray.map(minutes => moment({
  hour,
  minutes
}));
const timeOptions = Array.from({
  length: 24
}, (_, hour) => buildTimes(hour)).flat();
const parseTimeAdvanced = text => moment(text, ['hmma', 'ha', 'h:mma']);
const calculateDropdownNavigation = (timeString, timeNotation) => {
  const time = parseTimeAdvanced(timeString);
  if (!time.isValid()) {
    return {
      prevValue: formatTime(moment({
        hours: 0,
        minutes: 0
      }), timeNotation),
      activeIndex: -1,
      scrolledIndex: 0,
      nextValue: formatTime(moment({
        hours: 0,
        minutes: 0
      }), timeNotation)
    };
  }
  const hours = time.hours();
  const minutes = time.minutes();
  const minutesRounded = Math.floor(minutes / minuteStep) * minuteStep;
  const activeIndex = hours * intervalsPerHour + minutesRounded / minuteStep;
  if (minutes === minutesRounded) {
    return {
      prevValue: formatTime(moment({
        hours,
        minutes: minutesRounded
      }).subtract(minuteStep, 'minutes'), timeNotation),
      activeIndex,
      scrolledIndex: activeIndex,
      nextValue: formatTime(moment({
        hours,
        minutes: minutesRounded
      }).add(minuteStep, 'minutes'), timeNotation)
    };
  }
  return {
    prevValue: formatTime(moment({
      hours,
      minutes: minutesRounded
    }), timeNotation),
    activeIndex: -1,
    scrolledIndex: activeIndex,
    nextValue: formatTime(moment({
      hours,
      minutes: minutesRounded
    }).add(minuteStep, 'minutes'), timeNotation)
  };
};
const dropdownBodyCss = "dnyyljq";
const DropdownBody = forwardRef((props, ref) => <Menu ref={ref} {...props} className={classNames(dropdownBodyCss, props.className)} />);
const dropdownItemIsActiveCss = "d75glhq";
const dropdownItemCss = "dxakfrb";
const DropdownItem = forwardRef((props, ref) => {
  const {
    isActive,
    ...restProps
  } = props;
  return <Item ref={ref} {...restProps} className={classNames(dropdownItemCss, {
    [dropdownItemIsActiveCss]: isActive
  }, props.className)} />;
});
const scrollContainerIntoView = containerRef => {
  const rect = containerRef.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  if (rect.bottom > windowHeight) {
    containerRef.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest'
    });
  }
};
const scrollItemIntoView = (itemIndex, container) => {
  const node = container;
  const itemHeight = node.children[0].offsetHeight;
  const itemsToShowBefore = 2;
  node.scrollTop = (itemIndex - itemsToShowBefore) * itemHeight;
};
const TimesDropdown = ({
  onChange,
  options,
  activeIndex,
  scrolledIndex,
  dropdownId
}) => {
  const elRef = useRef();
  useLayoutEffect(() => {
    scrollItemIntoView(scrolledIndex, elRef.current);
    scrollContainerIntoView(elRef.current);
  }, [scrolledIndex, options]);
  const preventTextInputBlur = e => e.preventDefault();
  return <DropdownBody ref={elRef} tabIndex="-1" onMouseDown={preventTextInputBlur} role="listbox" id={dropdownId} aria-label={I18n.t('publisher.event_types.availability.time_options')}>
      {options.map((option, index) => <DropdownItem key={option} tabIndex="-1" onMouseDown={e => {
      preventTextInputBlur(e);
      onChange(option, e);
    }} isActive={index === activeIndex} role="option">
          {option}
        </DropdownItem>)}
    </DropdownBody>;
};
const SmartTimesDropdown = ({
  onChange,
  value,
  times,
  timeNotation,
  dropdownId
}) => {
  const options = useMemo(() => times.map(time => formatTime(time, timeNotation)), [times, timeNotation]);
  const {
    activeIndex,
    scrolledIndex
  } = useMemo(() => calculateDropdownNavigation(value, timeNotation), [value, timeNotation]);
  return <TimesDropdown onChange={onChange} options={options} activeIndex={activeIndex} scrolledIndex={scrolledIndex} dropdownId={dropdownId} />;
};
export const timeInputContainerCss = "t1bgmidu";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(timeInputContainerCss, props.className)} />;
});
const parseValue = (value, timeNotation) => {
  const time = moment(value, 'HH:mm');
  return time.isValid() ? formatTime(time, timeNotation) : value;
};
export const TimeInput = ({
  value,
  onChange,
  ...props
}) => {
  const timeNotation = useSelector(timeNotationSelector);
  const [localValue, setLocalValue] = useState(parseValue(value, timeNotation));
  const [toggleDropdown, DropdownContainer, isDropdownOpen] = useDropdown();
  const dropdownId = nanoid(4);
  const openDropdown = e => !isDropdownOpen && toggleDropdown(e);
  const closeDropdown = e => isDropdownOpen && toggleDropdown(e);
  const isPhone = useDeviceType('isPhone');
  useEffect(() => {
    const newLocalValue = parseValue(value, timeNotation);
    setLocalValue(newLocalValue);
  }, [value, timeNotation]);
  const applyChanges = timeString => {
    const time = parseTimeAdvanced(timeString);
    if (time.isValid()) {
      setLocalValue(formatTime(time, timeNotation));
    }
    const newValue = time.isValid() ? formatTime(time, '24h') : timeString;
    if (newValue !== value) {
      onChange(newValue);
    }
  };
  const keyHandlers = {
    Enter: e => {
      applyChanges(localValue);
      closeDropdown(e);
    },
    ArrowDown: e => {
      openDropdown(e);
      const {
        nextValue
      } = calculateDropdownNavigation(localValue, timeNotation);
      setLocalValue(nextValue);
    },
    ArrowUp: e => {
      openDropdown(e);
      const {
        prevValue
      } = calculateDropdownNavigation(localValue, timeNotation);
      setLocalValue(prevValue);
    },
    Tab: e => {
      closeDropdown(e);
    }
  };
  return <Container>
      <TextInput {...props} as={Input} autoComplete="off" autoCorrect="off" value={localValue} onChange={e => setLocalValue(e.target.value)} onBlur={() => applyChanges(localValue)} onClick={e => openDropdown(e)} onKeyDown={e => {
      keyHandlers[e.key]?.(e);
    }} aria-expanded={isDropdownOpen} role="combobox" aria-controls={dropdownId} />
      {!isPhone && <DropdownContainer>
          <SmartTimesDropdown onChange={applyChanges} value={localValue} times={timeOptions} timeNotation={timeNotation} dropdownId={dropdownId} />
        </DropdownContainer>}
    </Container>;
};
TimeInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

require("./time_input.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./time_input.js");